<template>
  <tool-bar class="asset-button">
    <template #content>
      <button :disabled="!showJakartoPointclouds" :class="['measure-button', { 'measure-button-activated': areToolsVisible }]" @click="toggleMeasureTools">
        <font-awesome-icon icon="draw-polygon" :class="[areToolsVisible ? 'measure-icon-activated' : 'measure-icon']"/>
      </button>
      
      <div v-if="showJakartoPointclouds"
        :class="[
          'draw-selection__tools',
          {
            'draw-selection__tools--visible':
              areToolsVisible
          },
        ]"
      >
        <tool-item @click="addLineFeature">
          <template #icon>
            <icon-draw-line
              :color="isDrawLineActive ? enabledColor : disabledColor"
              :class="'tool-item__button--reduced'"
            ></icon-draw-line>
          </template>
          <template #tooltip>
            Add a measure
          </template>
        </tool-item>
        <tool-item @click="addPolygonFeature">
          <template #icon>
            <icon-draw-polygon
              :color="isDrawPolygonActive ? enabledColor : disabledColor"
              :class="'tool-item__button--reduced'"
            ></icon-draw-polygon>
          </template>
          <template #tooltip>
            Add an area
          </template>
        </tool-item>
        <tool-item @click="deleteFeatures">
          <template #icon>
            <icon-bin :color="disabledColor"></icon-bin>
          </template>
          <template #tooltip>
            Delete all measures
          </template>
        </tool-item>
      </div>
    </template>
  </tool-bar>
</template>

<script setup>
import { ref, toRef, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import IconDrawLine from '@/components/icons/IconDrawLine.vue'
import IconDrawPolygon from '@/components/icons/IconDrawPolygon.vue'
import IconBin from '@/components/icons/IconBin.vue'
import useDrawStore from '@/stores/draw'
import { useCesiumMeasurement } from '@/composables/useMeasurement'
import { ToolBar, ToolItem } from '@jakarto3d/jakui'


const props = defineProps({
    viewer: Object,
    showJakartoPointclouds: Boolean,
    jakartoPointclouds: Object
})

const drawStore = useDrawStore()
const { isDrawLineActive, isDrawPolygonActive, onDelete } =
  storeToRefs(drawStore)
const areToolsVisible = ref(false)

// Initialize measurement composable
const {
  startMeasurement,
  stopMeasurement,
  clearAll,
  cleanup
} = useCesiumMeasurement(toRef(props, 'viewer'), toRef(props, 'jakartoPointclouds'))

const toggleMeasureTools = () => {
  areToolsVisible.value = !areToolsVisible.value

  if (!areToolsVisible.value) {
    stopMeasurement()
    isDrawLineActive.value = false
    isDrawPolygonActive.value = false
  }
}

const addLineFeature = () => {
  isDrawLineActive.value = !isDrawLineActive.value

  if (isDrawLineActive.value) {
    isDrawPolygonActive.value = false
    startMeasurement('distance')
  } else {
    stopMeasurement()
  }
}

const addPolygonFeature = () => {
  isDrawPolygonActive.value = !isDrawPolygonActive.value

  if (isDrawPolygonActive.value) {
    isDrawLineActive.value = false
    startMeasurement('area')
  } else {
    stopMeasurement()
  }
}

const deleteFeatures = () => {
  clearAll()
  onDelete.value = true
  isDrawLineActive.value = false
  isDrawPolygonActive.value = false
}

const enabledColor = getComputedStyle(
  document.documentElement,
).getPropertyValue('--secondary')
const disabledColor = getComputedStyle(
  document.documentElement,
).getPropertyValue('--primary')

onUnmounted(() => {
  cleanup()
  isDrawLineActive.value = false
  isDrawPolygonActive.value = false
})
</script>

<style lang="scss" scoped>
.tool-bar {
  position: absolute;
  top: 100px;
  right: 10px;
  z-index: 1020;
  display: flex;
  flex-direction: column;
  align-items: end;
  border-radius: 8px;;
}

.draw-selection__tools {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 1;

  width: 40px;
  border-radius: 12px 0px 12px 12px;
  padding: 5px 0px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  background: var(--panel-background);
  backdrop-filter: var(--panel-backdrop);
  box-shadow: var(--panel-shadow);
  opacity: 0;
  visibility: hidden;

  transform: scale(0.75) translate3d(calc(-100% - 10px), 0px, 0px);
  transform-origin: top;
  transition: 125ms ease-in-out;
}

.draw-selection__tools--visible {
  opacity: 1;
  visibility: visible;

  transform: scale(1) translate3d(calc(-100% - 10px), 10px, 0px);
}

.tool-item__button--reduced {
  width: 16px;
  height: 16px;
}

.measure-icon {
  font-size: 20px;
  color: var(--primary);
}

.measure-icon-activated {
  font-size: 20px;
  color: white;
}

.measure-button {
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.measure-button:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  background-color: color-mix(in srgb, var(--primary), transparent 70%);
}

.measure-button-activated {
  background-color: var(--primary);
  border: 1px solid #ffffff50 !important;
}


.measure-button:disabled {
  background-color: rgba(42, 42, 42, 0.4);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

.measure-button:disabled .measure-icon {
  font-size: 20px;
  color: white;
}

.snap-indicator {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  pointer-events: none;
}

.snap-hint {
  display: block;
  white-space: nowrap;
}
</style>
